import React, { useEffect, useRef, useState } from 'react';
import { Phone, ArrowUpRight, Award } from 'lucide-react';
import PurePromoLogo from '../components/PurePromoLogo';
import PartnerRow from '../components/PartnerRow';
import ContactCard from '../components/ContactCard';
import NoticeCard from '../components/NoticeCard';
import ServicesSection from '../components/ServicesSection';
import ScrollProgress from '../components/ScrollProgress';
import BackToTop from '../components/BackToTop';
import AnnouncementBar from '../components/AnnouncementBar';
import WelcomeModal from '../components/WelcomeModal';
import { useInView, useCountUp } from '../hooks/useMotion';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1762401244552-9eb61a7f9416?auto=format&fit=crop&w=1400&q=80';

/* --- Small animated stat with count-up when in view --- */
const Stat = ({ target, suffix = '', label, active }) => {
  const value = useCountUp(target, { duration: 1400, active });
  return (
    <div>
      <div className="text-[22px] font-black tracking-tight text-[#111111] tabular-nums">
        {value}{suffix}
      </div>
      <div className="text-[12px] text-slate-500 mt-1">{label}</div>
    </div>
  );
};

const LandingPage = () => {
  /* Sticky header condensation on scroll */
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Hero spotlight follows cursor */
  const heroRef = useRef(null);
  const onHeroMove = (e) => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  /* Reveal triggers for lower sections */
  const [statsRef, statsIn] = useInView({ threshold: 0.4 });
  const [partnerRef, partnerIn] = useInView({ threshold: 0.15 });
  const [infoRef, infoIn] = useInView({ threshold: 0.15 });
  const [closingRef, closingIn] = useInView({ threshold: 0.2 });

  return (
    <main className="min-h-screen w-full bg-white text-[#111111]">
      <ScrollProgress />
      <AnnouncementBar />

      {/* Sticky Header */}
      <header className={`pp-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="max-w-[1240px] mx-auto px-6 sm:px-8 lg:px-10 py-4 flex items-center justify-between pp-fade">
          <PurePromoLogo size="md" />
          <div className="hidden sm:flex items-center gap-6 text-[13.5px] text-slate-500">
            <a href="tel:+4915153764622" className="hover:text-[#111111] transition">015153764622</a>
            <span className="w-px h-4 bg-slate-200" />
            <span>Hamburg, DE</span>
            <a
              href="mailto:info@purepromo.de"
              className="pp-btn inline-flex items-center h-9 px-4 rounded-full bg-[#111111] text-white text-[12.5px] font-semibold hover:bg-black"
            >
              Kontakt
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-[1240px] w-full mx-auto px-6 sm:px-8 lg:px-10">

        {/* Hero */}
        <section
          ref={heroRef}
          onMouseMove={onHeroMove}
          className="pp-spotlight relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 pt-8 lg:pt-14"
        >
          {/* Ambient decorative blob (very subtle) */}
          <div className="pp-blob pointer-events-none absolute -z-0 -top-10 -left-16 w-[420px] h-[420px] rounded-full bg-[#EFEBFE] blur-3xl opacity-60" />

          {/* Left */}
          <div className="lg:col-span-7 flex flex-col justify-center relative">
            <div className="pp-fade-up pp-delay-1 inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.22em] text-[#6E56E7] mb-6">
              <Award className="w-3.5 h-3.5" strokeWidth={2.4} />
              SEIT ÜBER 15 JAHREN
            </div>

            <h1 className="pp-fade-up pp-delay-2 text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.03] font-black tracking-[-0.02em] text-[#111111]">
              Ihr zuverlässiger <span className="text-[#6E56E7]">Partner</span> für Promotion &amp; Vertrieb.
            </h1>

            <p className="pp-fade-up pp-delay-3 mt-6 text-[16px] sm:text-[17px] leading-relaxed text-slate-500 max-w-[640px]">
              Bei <span className="font-semibold text-[#111111]">PurePromo</span> stehen Erfahrung, Qualität und persönliche Betreuung seit über 15 Jahren im Mittelpunkt. Aktuell überarbeiten wir unsere Website, um Ihnen schon bald ein modernes und noch besseres Online-Erlebnis zu bieten.
            </p>

            {/* CTA */}
            <div className="pp-fade-up pp-delay-4 mt-8 flex flex-wrap items-center gap-3">
              <a
                href="mailto:info@purepromo.de"
                className="pp-btn group inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#111111] text-white text-[14px] font-semibold hover:bg-[#000] shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]"
              >
                Kontakt aufnehmen
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="tel:+4915153764622"
                className="pp-btn inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-[#111111] text-[14px] font-semibold border border-[#E7E8EC] hover:border-[#111111]"
              >
                <Phone className="w-4 h-4" />
                Anrufen
              </a>
              <div className="hidden sm:flex items-center gap-2 pl-2 text-[12.5px] text-slate-500">
                <span className="relative inline-flex">
                  <span className="w-2 h-2 rounded-full bg-[#6E56E7] pp-ping" />
                </span>
                Verfügbar Mo–Fr, 09–18 Uhr
              </div>
            </div>

            {/* Trust bullets with animated counters */}
            <div
              ref={statsRef}
              className={`pp-reveal ${statsIn ? 'pp-reveal-in' : ''} mt-10 grid grid-cols-3 gap-4 max-w-[560px]`}
            >
              <Stat target={15}  suffix="+" label="Jahre Erfahrung" active={statsIn} />
              <Stat target={100} suffix="%" label="bundesweit im Einsatz" active={statsIn} />
              <Stat target={7}   suffix="+" label="Partnermarken" active={statsIn} />
            </div>
          </div>

          {/* Right: hero image */}
          <div className="lg:col-span-5 relative z-[1]">
            <div className="relative">
              <div className="pp-image-in relative w-full aspect-[4/5] rounded-[28px] overflow-hidden bg-[#F6F7F9] border border-[#E7E8EC] shadow-[0_40px_80px_-40px_rgba(17,17,17,0.35)]">
                <img
                  src={HERO_IMAGE}
                  alt="Beratung im Mobilfunkshop"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
                  draggable={false}
                />
                {/* Top gradient for chip legibility */}
                <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/25 to-transparent" />
                {/* Bottom gradient */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/25 to-transparent" />

                {/* Top-right chip (inside image, no clipping) */}
                <div className="absolute top-5 right-5 inline-flex items-center gap-2 px-3 h-8 rounded-full bg-[#111111]/85 backdrop-blur text-white text-[12px] font-semibold">
                  <span className="relative flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6E56E7] pp-ping" />
                  </span>
                  Deutschlandweit
                </div>

                {/* Bottom-left chip */}
                <div className="absolute left-5 bottom-5 inline-flex items-center gap-2 px-3 h-8 rounded-full bg-white/95 backdrop-blur border border-white/60 text-[12px] font-semibold text-[#111111]">
                  <span className="relative flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6E56E7] pp-ping" />
                  </span>
                  Promotion vor Ort
                </div>
              </div>

              {/* Floating award chip – outside the clipped image */}
              <div className="hidden md:flex absolute -bottom-4 -left-4 items-center gap-2 h-11 px-4 rounded-full bg-white border border-[#E7E8EC] shadow-[0_18px_35px_-18px_rgba(17,17,17,0.4)]">
                <Award className="w-4 h-4 text-[#6E56E7]" />
                <span className="text-[12.5px] font-semibold text-[#111111]">15+ Jahre Erfahrung</span>
              </div>
            </div>
          </div>
        </section>

        {/* Partner marquee */}
        <section
          ref={partnerRef}
          className={`pp-reveal ${partnerIn ? 'pp-reveal-in' : ''} mt-14 lg:mt-20`}
        >
          <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-400 uppercase mb-5">
            Marken, mit denen wir arbeiten
          </div>
          <PartnerRow />
        </section>

        {/* Services */}
        <div className="mt-16 lg:mt-24">
          <ServicesSection />
        </div>

        {/* Info row: Notice + Contact */}
        <section
          ref={infoRef}
          className={`pp-reveal ${infoIn ? 'pp-reveal-in' : ''} mt-14 lg:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5`}
        >
          <NoticeCard />
          <ContactCard />
        </section>

        {/* Closing tagline */}
        <section
          ref={closingRef}
          className={`pp-reveal ${closingIn ? 'pp-reveal-in' : ''} mt-14 lg:mt-20`}
        >
          <div className="relative overflow-hidden rounded-[24px] border border-[#E7E8EC] bg-[#F6F7F9] px-6 sm:px-10 py-10 sm:py-14 text-center">
            {/* Decorative gradient blob */}
            <div className="pp-blob pointer-events-none absolute -z-0 -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#E9E2FE] blur-3xl opacity-70" />

            <div className="relative">
              <div className="text-[11px] font-semibold tracking-[0.22em] text-[#6E56E7] mb-3 uppercase">
                PurePromo
              </div>
              <p className="text-[22px] sm:text-[26px] lg:text-[30px] font-black tracking-[-0.02em] text-[#111111] max-w-[860px] mx-auto leading-[1.15]">
                Seit über 15 Jahren erfolgreich in <span className="text-[#6E56E7]">Promotion</span>, <span className="text-[#6E56E7]">Vertrieb</span> und <span className="text-[#6E56E7]">Marketing</span>.
              </p>
              <p className="mt-4 text-[14.5px] text-slate-500 max-w-[620px] mx-auto">
                Unsere neue Website ist bald online. Bis dahin stehen wir Ihnen selbstverständlich weiterhin persönlich für Anfragen und Projekte zur Verfügung.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <a
                  href="mailto:info@purepromo.de"
                  className="pp-btn group inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#111111] text-white text-[13.5px] font-semibold hover:bg-[#000]"
                >
                  Anfrage senden
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href="tel:+4915153764622"
                  className="pp-btn inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-[#111111] text-[13.5px] font-semibold border border-[#E7E8EC] hover:border-[#111111]"
                >
                  <Phone className="w-4 h-4" />
                  015153764622
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="pt-14 pb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-[#E7E8EC] pt-6">
            <div className="text-[13px] text-slate-500">
              © {new Date().getFullYear()} PurePromo. Alle Rechte vorbehalten.
            </div>
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
              <a href="#impressum" className="hover:text-[#111111] transition">Impressum</a>
              <a href="#datenschutz" className="hover:text-[#111111] transition">Datenschutz</a>
              <a href="tel:+4915153764622" className="hover:text-[#111111] transition">Telefon</a>
              <a href="mailto:info@purepromo.de" className="hover:text-[#111111] transition">E-Mail</a>
            </nav>
          </div>
        </footer>
      </div>

      <BackToTop />
      <WelcomeModal />
    </main>
  );
};

export default LandingPage;
